import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { QualifyingQuestion as QualifyingQuestionType } from "@/types/jobAdsTypes";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState } from "react";

interface QualifyingQuestionProps {
  question: QualifyingQuestionType;
  index: number;
  onQuestionChange: (question: QualifyingQuestionType) => void;
  onDelete: () => void;
}

export const QualifyingQuestion = ({
  question,
  index,
  onQuestionChange,
  onDelete,
}: QualifyingQuestionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group rounded-lg border bg-card text-card-foreground transition-all duration-200",
        isDragging
          ? "rotate-1 scale-[1.02] shadow-lg"
          : "shadow-sm hover:shadow",
        !isOpen && "hover:border-primary/50"
      )}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-start gap-3 p-3">
          <div className="flex flex-col items-center gap-2">
            <button
              {...attributes}
              {...listeners}
              className="mt-2 touch-none rounded-sm p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-accent"
              >
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                <span className="sr-only">
                  {isOpen ? "Dölj detaljer" : "Visa detaljer"}
                </span>
              </Button>
            </CollapsibleTrigger>
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <Label
                  htmlFor={`question-${index}`}
                  className="text-xs font-normal text-muted-foreground"
                >
                  Fråga {index + 1}
                </Label>
                <Textarea
                  id={`question-${index}`}
                  className="resize-none focus-visible:ring-0 min-h-[32px]"
                  value={question.question}
                  onChange={(e) => {
                    onQuestionChange({
                      ...question,
                      question: e.target.value,
                    });
                  }}
                  rows={1}
                  placeholder="Skriv din fråga här..."
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="h-8 w-8 p-0 text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                >
                  ×<span className="sr-only">Ta bort fråga</span>
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`required-${index}`}
                checked={question.isRequired}
                onChange={(e) => {
                  onQuestionChange({
                    ...question,
                    isRequired: e.target.checked,
                  });
                }}
                className="rounded border-gray-300 focus:ring-primary"
              />
              <Label
                htmlFor={`required-${index}`}
                className="text-sm text-muted-foreground"
              >
                Obligatorisk fråga
              </Label>
            </div>
          </div>
        </div>

        <CollapsibleContent>
          <div className="border-t bg-accent/5 p-3">
            <div className="grid gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <Label className="text-sm font-normal">
                    Graftus AI:s svar vid "Ja"
                  </Label>
                </div>
                <Textarea
                  className="min-h-[40px] bg-background"
                  value={question.onPositiveResponse}
                  onChange={(e) => {
                    onQuestionChange({
                      ...question,
                      onPositiveResponse: e.target.value,
                    });
                  }}
                  placeholder="Skriv ett positivt svar från AI..."
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-destructive" />
                  <Label className="text-sm font-normal">
                   Graftus AI:s svar vid "Nej"
                  </Label>
                </div>
                <Textarea
                  className="min-h-[32px] bg-background"
                  value={question.onNegativeResponse}
                  onChange={(e) => {
                    onQuestionChange({
                      ...question,
                      onNegativeResponse: e.target.value,
                    });
                  }}
                  placeholder="Skriv ett negativt svar från AI..."
                />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default QualifyingQuestion;
